if (!customElements.get('corporate-order-details')) {
  /* Shared with corporate-gifts.js — both read/write the same basket. */
  const BASKET_KEY = 'wildgrain-corporate-basket';
  const LISTS_KEY = 'wildgrain-corporate-recipient-lists';
  const BUYER_KEY = 'wildgrain-corporate-buyer';
  const SCHEDULE_KEY = 'wildgrain-corporate-schedule';
  const DATE_YMD = /^\d{4}-\d{2}-\d{2}$/;
  const MAX_RECIPIENTS = 500;
  const RECIPIENT_NOTE_MAX = 240;
  const GIFT_NOTE_NAME_MAX = 14;
  const LOGO_MAX_BYTES = 2 * 1024 * 1024;
  const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
  /* Some OS/browser combos report an empty MIME type — fall back to the extension. */
  const LOGO_EXTENSION_REGEX = /\.(png|jpe?g|webp|svg)$/i;

  const ymdLocal = (date) => {
    const d = date || new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  };

  const isWeekendYmd = (ymd) => {
    const parts = String(ymd || '').split('-').map(Number);
    if (parts.length < 3 || !parts[0]) return false;
    const day = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])).getUTCDay();
    return day === 0 || day === 6;
  };
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  /* Same NANP rules as normalizeUsPhone in the API's corporate/service.js —
     Shopify rejects anything it can't parse as a phone number. Checking here is
     what keeps a bad phone on its own row instead of surfacing as one long
     server error under Pay Now. */
  const NANP_NATIONAL_REGEX = /^[2-9]\d{2}[2-9]\d{6}$/;
  const CSV_HEADERS =
    'First Name,Last Name,Recipient Email (optional),Recipient Cell Phone Number,Address 1,Address 2,City,State,Zip Code,Gift Note (optional; maximum is 240 characters including spaces & no emojis)';
  const EXCEL_NAME_REGEX = /\.xlsx?$/i;
  const SHEETJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
  let sheetJsLoad = null;

  const isExcelFile = (file) => {
    const name = String((file && file.name) || '');
    if (EXCEL_NAME_REGEX.test(name)) return true;
    const type = String((file && file.type) || '').toLowerCase();
    return (
      type === 'application/vnd.ms-excel' ||
      type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );
  };

  const loadSheetJs = () => {
    if (window.XLSX) return Promise.resolve(window.XLSX);
    if (sheetJsLoad) return sheetJsLoad;
    sheetJsLoad = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SHEETJS_SRC;
      script.async = true;
      script.onload = () => {
        if (window.XLSX) resolve(window.XLSX);
        else reject(new Error('xlsx missing'));
      };
      script.onerror = () => {
        sheetJsLoad = null;
        reject(new Error('xlsx load failed'));
      };
      document.head.appendChild(script);
    });
    return sheetJsLoad;
  };

  const padUsZip = (value) => {
    const raw = String(value || '').trim();
    if (/^\d{3,4}$/.test(raw)) return raw.padStart(5, '0');
    return raw;
  };

  const stringifySheetRow = (row) =>
    (Array.isArray(row) ? row : []).map((cell) => String(cell == null ? '' : cell).trim());
  const REQUIRED_RECIPIENT_FIELDS = [
    'firstName',
    'lastName',
    'address1',
    'city',
    'state',
    'zip',
  ];
  const RECIPIENT_FIELD_LABELS = {
    firstName: 'first name',
    lastName: 'last name',
    email: 'email',
    phone: 'phone number',
    address1: 'address',
    city: 'city',
    state: 'state',
    zip: 'ZIP code',
  };

  /* Same rule the subscription checkout enforces through Shopify shipping
     zones: frozen boxes can't reach AK / HI / PR. Mirrors NON_SHIPPABLE_STATES
     in the API's corporate/service.js, which re-checks at checkout. CSV uploads
     say "Hawaii" as often as "HI", so full names are folded to codes first. */
  const NON_SHIPPABLE_STATES = { AK: 'Alaska', HI: 'Hawaii', PR: 'Puerto Rico' };
  const US_STATE_CODES = {
    alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
    colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
    hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
    kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
    massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO',
    montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
    'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
    oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
    virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
    'district of columbia': 'DC', 'washington dc': 'DC', 'puerto rico': 'PR',
  };
  const normalizeStateCode = (state) => {
    const raw = String(state || '').trim();
    if (!raw) return '';
    if (raw.length === 2) return raw.toUpperCase();
    return US_STATE_CODES[raw.toLowerCase().replace(/\s+/g, ' ')] || raw;
  };
  const nonShippableStateName = (state) => NON_SHIPPABLE_STATES[normalizeStateCode(state)] || '';

  const isValidUsPhone = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return true;
    const digits = value.replace(/\D/g, '');
    const national = digits.length === 11 && digits.charAt(0) === '1' ? digits.slice(1) : digits;
    if (national.length !== 10 || !NANP_NATIONAL_REGEX.test(national)) return false;
    /* 555 exchanges are reserved for fiction and Shopify won't take them. */
    return national.slice(0, 3) !== '555' && national.slice(3, 6) !== '555';
  };

  /* Gift note personalization. Mirrors renderGiftNote in the API's
     corporate/service.js, which is the source of truth — the note is saved and
     sent with its tokens intact and the server substitutes when it builds each
     Shopify order, so a later name edit still comes out right. These helpers
     exist only to preview and validate before checkout. */
  const GIFT_NOTE_TOKENS = ['first_name', 'last_name'];
  const GIFT_NOTE_TOKEN_REGEX = /\{\{([^{}]*)\}\}/g;
  const GIFT_NOTE_PREVIEW_SAMPLE = { firstName: 'Alex', lastName: 'Rivera' };

  const giftNoteTokenName = (inner) =>
    String(inner || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');

  const giftNoteHasTokens = (raw) => /\{\{[^{}]*\}\}/.test(String(raw || ''));

  const giftNoteUnknownTokens = (raw) => {
    const note = String(raw || '');
    const unknown = [];
    GIFT_NOTE_TOKEN_REGEX.lastIndex = 0;
    let match = GIFT_NOTE_TOKEN_REGEX.exec(note);
    while (match !== null) {
      const name = giftNoteTokenName(match[1]);
      if (GIFT_NOTE_TOKENS.indexOf(name) === -1 && unknown.indexOf(name) === -1) unknown.push(name);
      match = GIFT_NOTE_TOKEN_REGEX.exec(note);
    }
    GIFT_NOTE_TOKEN_REGEX.lastIndex = 0;
    return unknown;
  };

  /* {{first_name}} / {{last_name}} each count as 14 toward the 240 limit. */
  const giftNoteMessageLength = (raw) => {
    const note = String(raw || '');
    GIFT_NOTE_TOKEN_REGEX.lastIndex = 0;
    const counted = note.replace(GIFT_NOTE_TOKEN_REGEX, (full, inner) => {
      const name = giftNoteTokenName(inner);
      return GIFT_NOTE_TOKENS.indexOf(name) !== -1 ? ' '.repeat(GIFT_NOTE_NAME_MAX) : full;
    });
    GIFT_NOTE_TOKEN_REGEX.lastIndex = 0;
    return counted.length;
  };

  /* A token that resolves to nothing leaves gaps: "Enjoy, {{first_name}}!"
     with no name would read "Enjoy, !". Only applied when something was
     actually substituted, so plain notes are left exactly as typed. */
  const tidyGiftNoteSpacing = (value) =>
    String(value || '')
      .replace(/[ \t]+/g, ' ')
      .replace(/ ([,.!?;:])/g, '$1')
      .replace(/([,;:])(?=[.!?])/g, '')
      .replace(/ +\n/g, '\n')
      .trim();

  /* Clicking two tokens in a row, or dropping one against a word, would render
     as "IsmailSalhi" / "DearIsmail". Openers and closers are left tight so
     "({{first_name}})" and "{{first_name}}," still read correctly. */
  const GIFT_NOTE_TIGHT_BEFORE = /[\s([{"'“‘\-/]$/;
  const GIFT_NOTE_TIGHT_AFTER = /^[\s,.!?;:)\]}"'’”\-/]/;

  const insertGiftNoteToken = (raw, token, start, end) => {
    const before = String(raw).slice(0, start);
    const after = String(raw).slice(end);
    const lead = before && !GIFT_NOTE_TIGHT_BEFORE.test(before) ? ' ' : '';
    const tail = after && !GIFT_NOTE_TIGHT_AFTER.test(after) ? ' ' : '';
    const insert = lead + token + tail;
    return { value: before + insert + after, caret: start + insert.length };
  };

  const renderGiftNote = (raw, recipient) => {
    if (raw == null || raw === '') return '';
    const source = recipient || {};
    const values = {
      first_name: String(source.firstName || '').trim().slice(0, GIFT_NOTE_NAME_MAX),
      last_name: String(source.lastName || '').trim().slice(0, GIFT_NOTE_NAME_MAX),
    };
    let substituted = false;
    const rendered = String(raw)
      /* Back-to-back tokens would run the names together ("IsmailSalhi"). */
      .replace(/\}\}(?=\{\{)/g, '}} ')
      .replace(GIFT_NOTE_TOKEN_REGEX, (full, inner) => {
        substituted = true;
        const name = giftNoteTokenName(inner);
        return Object.prototype.hasOwnProperty.call(values, name) ? values[name] : '';
      });
    return substituted ? tidyGiftNoteSpacing(rendered) : rendered;
  };

  class CorporateOrderDetails extends HTMLElement {
    connectedCallback() {
      this.itemsEl = this.querySelector('[data-cod-items]');
      this.emptyEl = this.querySelector('[data-cod-empty]');
      this.placedNoticeEl = this.querySelector('[data-cod-placed]');
      this.placedNoticeLinkEl = this.querySelector('[data-cod-placed-link]');
      this.summaryEl = this.querySelector('[data-cod-summary]');
      this.subtotalEl = this.querySelector('[data-cod-subtotal]');
      this.subtotalLabelEl = this.querySelector('[data-cod-subtotal-label]');
      this.taxRowEl = this.querySelector('[data-cod-tax-row]');
      this.taxEl = this.querySelector('[data-cod-tax]');
      this.form = this.querySelector('[data-cod-form]');
      this.payNowBtn = this.querySelector('[data-cod-pay-now]');
      this.payErrorEl = this.querySelector('[data-cod-pay-error]');
      this.buyerEmailInput = this.querySelector('[data-cod-buyer-email]');
      this.buyerCompanyInput = this.querySelector('[data-cod-buyer-company]');
      this.buyerNameInput = this.querySelector('[data-cod-buyer-name]');
      this.buyerPhoneInput = this.querySelector('[data-cod-buyer-phone]');
      this.shipDateInput = this.querySelector('[data-cod-ship-date]');
      this.shipClearBtn = this.querySelector('[data-cod-ship-clear]');
      this.shipHintEl = this.querySelector('[data-cod-ship-hint]');
      this.shipWeekendEl = this.querySelector('[data-cod-ship-weekend]');
      this.shipErrorEl = this.querySelector('[data-cod-ship-error]');
      this.logoFileInput = this.querySelector('[data-cod-logo-file]');
      this.logoPreviewEl = this.querySelector('[data-cod-logo-preview]');
      this.logoImageEl = this.querySelector('[data-cod-logo-image]');
      this.logoTriggerEl = this.querySelector('[data-cod-logo-trigger]');
      this.logoTriggerTextEl = this.querySelector('[data-cod-logo-trigger-text]');
      this.logoErrorEl = this.querySelector('[data-cod-logo-error]');
      this.logoUrl = '';
      this.listsEl = this.querySelector('[data-cod-lists]');
      this.listErrorsEl = this.querySelector('[data-cod-list-errors]');
      this.listModal = this.querySelector('[data-cod-list-modal]');
      this.listModalTitle = this.querySelector('[data-cod-list-title]');
      this.listErrorEl = this.querySelector('[data-cod-list-error]');
      this.csvErrorEl = this.querySelector('[data-cod-csv-error]');
      this.recipientForm = this.querySelector('[data-cod-recipient-form]');
      this.recipientNote = this.querySelector('[data-cod-recipient-note]');
      this.recipientStateErrorEl = this.querySelector('[data-cod-state-error]');
      this.recipientNoteCounter = this.querySelector('[data-cod-recipient-note-counter]');
      this.recipientNotePreview = this.querySelector('[data-cod-recipient-note-preview]');
      this.bulkNoteEl = this.querySelector('[data-cod-bulk-note]');
      this.bulkNoteInput = this.querySelector('[data-cod-bulk-note-input]');
      this.bulkNoteCounter = this.querySelector('[data-cod-bulk-note-counter]');
      this.bulkNotePreview = this.querySelector('[data-cod-bulk-note-preview]');
      this.bulkNoteStatus = this.querySelector('[data-cod-bulk-note-status]');
      this.bulkNoteConfirm = this.querySelector('[data-cod-bulk-note-confirm]');
      this.bulkNoteConfirmTitle = this.querySelector('[data-cod-bulk-note-confirm-title]');
      this.bulkNoteConfirmList = this.querySelector('[data-cod-bulk-note-confirm-list]');
      this.bulkNoteConfirmApply = this.querySelector('[data-cod-bulk-note-confirm-apply]');
      this.csvTemplateLink = this.querySelector('[data-cod-csv-template]');
      this.fileInput = this.querySelector('[data-cod-list-file]');
      this.stepEls = {
        method: this.querySelector('[data-cod-step="method"]'),
        manual: this.querySelector('[data-cod-step="manual"]'),
        csv: this.querySelector('[data-cod-step="csv"]'),
      };
      this.methodCards = this.querySelectorAll('[data-cod-method]');
      this.selectedMethod = 'manual';
      this.draftRecipients = [];
      this.buyerEmailTouched = false;
      this.buyerNameTouched = false;
      this.buyerPhoneTouched = false;
      this.validationAttempted = false;
      this.handleModalKeydown = (event) => {
        if (event.key === 'Escape') this.closeListModal();
      };

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleQuantityInput.bind(this));

      if (this.fileInput) {
        this.fileInput.addEventListener('change', this.handleFileUpload.bind(this));
      }

      if (this.logoFileInput) {
        this.logoFileInput.addEventListener('change', this.handleLogoUpload.bind(this));
      }

      if (this.form) {
        this.form.addEventListener('submit', this.handlePayNow.bind(this));
      }

      if (this.buyerEmailInput) {
        this.buyerEmailInput.addEventListener('input', this.handleBuyerInput.bind(this));
        this.buyerEmailInput.addEventListener('blur', this.handleBuyerEmailBlur.bind(this));
      }
      if (this.buyerCompanyInput) {
        this.buyerCompanyInput.addEventListener('input', this.handleBuyerInput.bind(this));
      }
      if (this.buyerNameInput) {
        this.buyerNameInput.addEventListener('input', this.handleBuyerInput.bind(this));
        this.buyerNameInput.addEventListener('blur', this.handleBuyerNameBlur.bind(this));
      }
      if (this.buyerPhoneInput) {
        this.buyerPhoneInput.addEventListener('input', this.handleBuyerInput.bind(this));
        this.buyerPhoneInput.addEventListener('blur', this.handleBuyerPhoneBlur.bind(this));
      }
      if (this.shipDateInput) {
        this.shipDateInput.addEventListener('change', this.handleScheduleChange.bind(this));
        this.shipDateInput.addEventListener('input', this.handleScheduleChange.bind(this));
      }
      if (this.shipClearBtn) {
        this.shipClearBtn.addEventListener('click', this.clearSchedule.bind(this));
      }

      if (this.recipientForm) {
        this.recipientForm.addEventListener('submit', (event) => event.preventDefault());
        this.recipientForm.addEventListener('input', this.handleRecipientFormInput.bind(this));
        /* <select> fires change, not input, in some browsers — and the state
           warning should appear the moment Hawaii is picked, not on save. */
        this.recipientForm.addEventListener('change', this.handleRecipientFormInput.bind(this));
      }

      if (this.csvTemplateLink) {
        this.csvTemplateLink.addEventListener('click', this.handleTemplateDownload.bind(this));
      }

      if (this.bulkNoteInput) {
        this.bulkNoteInput.addEventListener('input', this.handleBulkNoteInput.bind(this));
      }

      this.restoreBuyer();
      this.restoreSchedule();
      this.renderItems();
      this.renderLists();
      this.refreshInlineValidation();
      this.updatePayNowState();
      this.scheduleTaxEstimate();
      this.settlePlacedOrder();
    }

    /* Payment happens in Shopify checkout, so the only trace of a placed order
       is the token Pay Now stored. If that order is past draft, everything on
       this page belongs to it and is cleared for a fresh one. */
    async settlePlacedOrder() {
      const storage = window.WildgrainCorporateBasket;
      if (!storage) return;

      const pending = storage.readPending();
      if (!pending) return;
      if (!(await storage.settle(this.apiBase()))) return;

      this.showPlacedNotice(pending.token);
      this.resetBuyerForm();
      this.clearSchedule();
      this.renderItems();
      this.renderLists();
      this.refreshInlineValidation();
      this.updatePayNowState();
      this.scheduleTaxEstimate();
    }

    /* Storage is already gone by this point; the fields were filled in on page
       load and would otherwise keep showing the finished order's details. */
    resetBuyerForm() {
      if (this.buyerEmailInput) this.buyerEmailInput.value = '';
      if (this.buyerCompanyInput) this.buyerCompanyInput.value = '';
      if (this.buyerNameInput) this.buyerNameInput.value = '';
      if (this.buyerPhoneInput) this.buyerPhoneInput.value = '';
      this.setLogo('');
      /* An untouched blank form must not open with every field in red. */
      this.buyerEmailTouched = false;
      this.buyerNameTouched = false;
      this.buyerPhoneTouched = false;
      this.validationAttempted = false;
    }

    /* An empty basket on the way back from checkout reads as lost work without
       this — say the order is placed and link to its tracking page. */
    showPlacedNotice(token) {
      if (!this.placedNoticeEl) return;
      if (this.placedNoticeLinkEl) {
        if (token) {
          const url = new URL(this.placedNoticeLinkEl.getAttribute('href'), window.location.origin);
          url.searchParams.set('token', token);
          this.placedNoticeLinkEl.href = url.pathname + url.search;
        } else {
          this.placedNoticeLinkEl.hidden = true;
        }
      }
      this.placedNoticeEl.hidden = false;
    }

    /* Basket items */

    readBasket() {
      try {
        const items = JSON.parse(localStorage.getItem(BASKET_KEY));
        return Array.isArray(items) ? items : [];
      } catch (e) {
        return [];
      }
    }

    writeBasket(items) {
      try {
        localStorage.setItem(BASKET_KEY, JSON.stringify(items));
      } catch (e) {
        /* storage unavailable; changes live in memory for the session */
      }
      this.renderItems(items);
      this.renderLists();
      this.updatePayNowState();
      this.scheduleTaxEstimate();
    }

    /* Buyer (company details) — name and email required; company, phone, logo optional. */

    emptyBuyer() {
      return { email: '', company: '', name: '', phone: '', logoUrl: '' };
    }

    readBuyer() {
      try {
        const buyer = JSON.parse(localStorage.getItem(BUYER_KEY));
        if (buyer && typeof buyer === 'object') {
          return {
            email: typeof buyer.email === 'string' ? buyer.email : '',
            company: typeof buyer.company === 'string' ? buyer.company : '',
            name: typeof buyer.name === 'string' ? buyer.name : '',
            phone: typeof buyer.phone === 'string' ? buyer.phone : '',
            logoUrl: typeof buyer.logoUrl === 'string' ? buyer.logoUrl : '',
          };
        }
      } catch (e) {
        /* ignore */
      }
      return this.emptyBuyer();
    }

    writeBuyer(buyer) {
      try {
        localStorage.setItem(BUYER_KEY, JSON.stringify(buyer));
      } catch (e) {
        /* storage unavailable */
      }
    }

    restoreBuyer() {
      const buyer = this.readBuyer();
      if (this.buyerEmailInput && buyer.email) this.buyerEmailInput.value = buyer.email;
      if (this.buyerCompanyInput && buyer.company) this.buyerCompanyInput.value = buyer.company;
      if (this.buyerNameInput && buyer.name) this.buyerNameInput.value = buyer.name;
      if (this.buyerPhoneInput && buyer.phone) this.buyerPhoneInput.value = buyer.phone;
      this.setLogo(buyer.logoUrl || '');
    }

    getBuyerFromForm() {
      return {
        email: ((this.buyerEmailInput && this.buyerEmailInput.value) || '').trim(),
        company: ((this.buyerCompanyInput && this.buyerCompanyInput.value) || '').trim(),
        name: ((this.buyerNameInput && this.buyerNameInput.value) || '').trim(),
        phone: ((this.buyerPhoneInput && this.buyerPhoneInput.value) || '').trim(),
        logoUrl: this.logoUrl || '',
      };
    }

    handleBuyerInput() {
      const buyer = this.getBuyerFromForm();
      this.writeBuyer(buyer);
      this.refreshInlineValidation();
      this.updatePayNowState();
    }

    handleBuyerEmailBlur() {
      this.buyerEmailTouched = true;
      this.refreshInlineValidation();
      this.updatePayNowState();
    }

    handleBuyerNameBlur() {
      this.buyerNameTouched = true;
      this.refreshInlineValidation();
      this.updatePayNowState();
    }

    handleBuyerPhoneBlur() {
      this.buyerPhoneTouched = true;
      this.refreshInlineValidation();
      this.updatePayNowState();
    }

    emptySchedule() {
      return { date: '', kind: 'hold_until' };
    }

    readSchedule() {
      try {
        const raw = JSON.parse(localStorage.getItem(SCHEDULE_KEY));
        if (raw && typeof raw === 'object') {
          const date = DATE_YMD.test(raw.date || '') ? raw.date : '';
          return { date: date, kind: 'hold_until' };
        }
      } catch (e) {
        /* ignore */
      }
      return this.emptySchedule();
    }

    writeSchedule(schedule) {
      try {
        if (!schedule || !schedule.date) {
          localStorage.removeItem(SCHEDULE_KEY);
          return;
        }
        localStorage.setItem(SCHEDULE_KEY, JSON.stringify({
          date: schedule.date,
          kind: 'hold_until',
        }));
      } catch (e) {
        /* storage unavailable */
      }
    }

    getScheduleFromForm() {
      const date = ((this.shipDateInput && this.shipDateInput.value) || '').trim();
      return { date: DATE_YMD.test(date) ? date : '', kind: 'hold_until' };
    }

    restoreSchedule() {
      const schedule = this.readSchedule();
      if (this.shipDateInput && schedule.date) this.shipDateInput.value = schedule.date;
      this.refreshScheduleUi();
    }

    handleScheduleChange() {
      const schedule = this.getScheduleFromForm();
      this.writeSchedule(schedule);
      this.refreshScheduleUi();
    }

    clearSchedule() {
      if (this.shipDateInput) this.shipDateInput.value = '';
      this.writeSchedule(this.emptySchedule());
      this.refreshScheduleUi();
    }

    refreshScheduleUi() {
      const schedule = this.getScheduleFromForm();
      if (this.shipDateInput) this.shipDateInput.min = ymdLocal();
      if (this.shipClearBtn) this.shipClearBtn.hidden = !schedule.date;
      if (this.shipWeekendEl) this.shipWeekendEl.hidden = !isWeekendYmd(schedule.date);
      const error = this.scheduleErrorMessage(schedule);
      if (this.shipErrorEl) {
        this.shipErrorEl.textContent = error;
        this.shipErrorEl.hidden = !error;
      }
    }

    scheduleErrorMessage(schedule) {
      if (!schedule.date) return '';
      const today = ymdLocal();
      if (schedule.date < today) return this.t('schedulePast');
      return '';
    }

    isBuyerEmailValid(email) {
      return Boolean(email) && EMAIL_REGEX.test(email);
    }

    isBuyerNameValid(name) {
      return Boolean(String(name || '').trim());
    }

    isBuyerPhoneValid(phone) {
      return isValidUsPhone(phone);
    }

    getRecipientValidationEntry(recipient, globalIndex) {
      const issues = this.recipientIssues(recipient);
      const message = issues
        .map((issue) => this.formatRecipientIssue(recipient, globalIndex, issue))
        .join(' ');
      return { valid: issues.length === 0, message, issues };
    }

    allRecipientsValid() {
      let globalIndex = 0;
      let valid = true;
      this.readLists().forEach((list) => {
        (list.recipients || []).forEach((recipient) => {
          if (!this.getRecipientValidationEntry(recipient, globalIndex).valid) valid = false;
          globalIndex += 1;
        });
      });
      return valid;
    }

    shouldShowBuyerEmailError() {
      const email = this.getBuyerFromForm().email;
      if (this.isBuyerEmailValid(email)) return false;
      if (this.buyerEmailTouched || this.validationAttempted) return true;
      return this.totalRecipientCount() > 0;
    }

    shouldShowBuyerNameError() {
      const name = this.getBuyerFromForm().name;
      if (this.isBuyerNameValid(name)) return false;
      if (this.buyerNameTouched || this.validationAttempted) return true;
      return this.totalRecipientCount() > 0;
    }

    shouldShowBuyerPhoneError() {
      const phone = this.getBuyerFromForm().phone;
      if (this.isBuyerPhoneValid(phone)) return false;
      return this.buyerPhoneTouched || this.validationAttempted;
    }

    shouldHighlightRecipientErrors() {
      return this.totalRecipientCount() > 0;
    }

    setFieldError(input, showError) {
      if (!input) return;
      input.classList.toggle('cod-input--error', showError);
      if (showError) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }

    refreshBuyerFieldValidation() {
      this.setFieldError(this.buyerNameInput, this.shouldShowBuyerNameError());
      this.setFieldError(this.buyerEmailInput, this.shouldShowBuyerEmailError());
      this.setFieldError(this.buyerPhoneInput, this.shouldShowBuyerPhoneError());
    }

    updateValidationSummary() {
      if (this.paying) return;

      const recipientMessages = [];
      let globalIndex = 0;
      this.readLists().forEach((list) => {
        (list.recipients || []).forEach((recipient) => {
          const entry = this.getRecipientValidationEntry(recipient, globalIndex);
          if (!entry.valid && this.shouldHighlightRecipientErrors() && entry.message) {
            recipientMessages.push(entry.message);
          }
          globalIndex += 1;
        });
      });

      const shown = recipientMessages.slice(0, 4);
      if (recipientMessages.length > 4) {
        shown.push(this.t('csvMoreErrors').replace('__COUNT__', String(recipientMessages.length - 4)));
      }
      this.showListErrors(shown);

      if (this.shouldShowBuyerNameError()) {
        this.showPayError(this.t('buyerNameRequired'));
      } else if (this.shouldShowBuyerEmailError()) {
        this.showPayError(this.t('buyerEmailRequired'));
      } else if (this.shouldShowBuyerPhoneError()) {
        this.showPayError(this.t('buyerPhoneInvalid'));
      } else {
        this.showPayError('');
      }
    }

    showListErrors(messages) {
      if (!this.listErrorsEl) return;
      this.listErrorsEl.textContent = '';
      if (!messages.length) {
        this.listErrorsEl.hidden = true;
        return;
      }
      messages.forEach((text) => {
        const item = document.createElement('li');
        item.textContent = text;
        this.listErrorsEl.appendChild(item);
      });
      this.listErrorsEl.hidden = false;
    }

    refreshInlineValidation() {
      this.refreshBuyerFieldValidation();
      this.updateValidationSummary();
    }

    /* Company logo — uploaded to the API (GCS) as soon as it is picked, so
       the draft row created at Pay Now already carries the URL. */

    setLogo(url) {
      this.logoUrl = url || '';
      if (this.logoPreviewEl) this.logoPreviewEl.hidden = !this.logoUrl;
      if (this.logoImageEl) this.logoImageEl.src = this.logoUrl || '';
      if (this.logoTriggerTextEl) {
        this.logoTriggerTextEl.textContent = this.logoUrl ? this.t('logoReplace') : this.t('logoUpload');
      }
    }

    showLogoError(message) {
      if (!this.logoErrorEl) return;
      this.logoErrorEl.textContent = message || '';
      this.logoErrorEl.hidden = !message;
    }

    setLogoUploading(uploading) {
      this.logoUploading = Boolean(uploading);
      if (this.logoTriggerEl) {
        this.logoTriggerEl.classList.toggle('is-uploading', this.logoUploading);
        this.logoTriggerEl.setAttribute('aria-busy', this.logoUploading ? 'true' : 'false');
      }
      if (this.logoTriggerTextEl) {
        this.logoTriggerTextEl.textContent = this.logoUploading
          ? this.t('logoUploading')
          : this.logoUrl
            ? this.t('logoReplace')
            : this.t('logoUpload');
      }
      if (this.logoFileInput) this.logoFileInput.disabled = this.logoUploading;
    }

    isLogoFileTypeOk(file) {
      if (file.type) return LOGO_TYPES.indexOf(String(file.type).toLowerCase()) !== -1;
      return LOGO_EXTENSION_REGEX.test(file.name || '');
    }

    /* Files with no OS-reported MIME type would reach the API as
       application/octet-stream and be rejected — re-wrap them with the type
       implied by the (already validated) extension. */
    normalizeLogoFile(file) {
      if (file.type) return file;
      const match = (file.name || '').match(LOGO_EXTENSION_REGEX);
      if (!match) return file;
      const ext = match[1].toLowerCase();
      const type =
        ext === 'png'
          ? 'image/png'
          : ext === 'webp'
            ? 'image/webp'
            : ext === 'svg'
              ? 'image/svg+xml'
              : 'image/jpeg';
      try {
        return new File([file], file.name, { type: type });
      } catch (e) {
        return file;
      }
    }

    /* Turn an upload failure into a message the buyer can act on. `body` is
       null when the response wasn't JSON (proxy interstitial, HTML error page). */
    logoUploadErrorMessage(response, body) {
      const apiError = body && typeof body.error === 'string' ? body.error : '';
      if (response.status === 413 || apiError.indexOf('2MB') !== -1) return this.t('logoTooLarge');
      if (response.status === 429 || apiError === 'rate_limited') return this.t('logoRateLimited');
      if (apiError && apiError !== 'internal') return apiError;
      if (!body) {
        return this.t('logoUnexpected').replace('__STATUS__', String(response.status));
      }
      return this.t('logoError');
    }

    async handleLogoUpload(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file || this.logoUploading) return;

      if (!this.isLogoFileTypeOk(file)) {
        this.showLogoError(this.t('logoInvalidType'));
        return;
      }
      if (file.size > LOGO_MAX_BYTES) {
        this.showLogoError(this.t('logoTooLarge'));
        return;
      }

      const base = this.apiBase();
      if (!base) {
        this.showLogoError(this.t('apiMissing'));
        return;
      }

      this.showLogoError('');
      this.setLogoUploading(true);

      try {
        const formData = new FormData();
        formData.append('logo', this.normalizeLogoFile(file));

        let response;
        try {
          response = await fetch(base + '/api/corporate/logo-upload', {
            method: 'POST',
            headers: this.apiHeaders(),
            body: formData,
          });
        } catch (networkErr) {
          console.error('[corporate-order-details] logo upload network error:', networkErr);
          this.showLogoError(this.t('logoNetwork'));
          return;
        }

        let body = null;
        try {
          body = await response.json();
        } catch (e) {
          body = null;
        }

        if (!response.ok || !body || !body.ok || !body.url) {
          console.error(
            '[corporate-order-details] logo upload failed: status',
            response.status,
            body
          );
          this.showLogoError(this.logoUploadErrorMessage(response, body));
          return;
        }

        this.setLogo(body.url);
        this.writeBuyer(this.getBuyerFromForm());
      } catch (err) {
        console.error('[corporate-order-details] logo upload failed:', err);
        this.showLogoError(this.t('logoError'));
      } finally {
        this.setLogoUploading(false);
      }
    }

    removeLogo() {
      this.setLogo('');
      this.showLogoError('');
      this.writeBuyer(this.getBuyerFromForm());
    }

    canPayNow() {
      const buyer = this.getBuyerFromForm();
      return (
        this.isBuyerNameValid(buyer.name) &&
        this.isBuyerEmailValid(buyer.email) &&
        this.isBuyerPhoneValid(buyer.phone) &&
        this.readBasket().length > 0 &&
        this.totalRecipientCount() > 0 &&
        this.allRecipientsValid()
      );
    }

    updatePayNowState() {
      if (this.payNowBtn) this.payNowBtn.disabled = !this.canPayNow();
    }

    showPayError(message) {
      if (!this.payErrorEl) return;
      this.payErrorEl.textContent = message || '';
      this.payErrorEl.hidden = !message;
    }

    setPayLoading(loading) {
      this.paying = Boolean(loading);
      if (!this.payNowBtn) return;
      this.payNowBtn.classList.toggle('btn--loading', this.paying);
      this.payNowBtn.setAttribute('aria-busy', this.paying ? 'true' : 'false');
      if (this.paying) {
        this.payNowBtn.disabled = true;
        this.payNowBtn.textContent = this.t('payLoading');
      } else {
        this.payNowBtn.textContent = this.t('payNow');
        this.updatePayNowState();
      }
    }

    apiBase() {
      return (this.dataset.apiBase || '').replace(/\/$/, '');
    }

    /* Headers for Wildgrain API calls. Free ngrok tunnels (the documented dev
       API base) answer browser requests with an HTML interstitial unless this
       skip header is sent — the JSON parse then fails and every call looks
       like a server error. Only added for ngrok hosts. */
    apiHeaders(extra) {
      const headers = Object.assign({ Accept: 'application/json' }, extra || {});
      if (/\bngrok(-free)?\.(app|dev|io)\b/i.test(this.apiBase())) {
        headers['ngrok-skip-browser-warning'] = 'true';
      }
      return headers;
    }

    flattenRecipients() {
      const recipients = [];
      this.readLists().forEach((list) => {
        (list.recipients || []).forEach((r) => {
          const row = {
            first_name: r.firstName || '',
            last_name: r.lastName || '',
            email: r.email || '',
            phone: r.phone || '',
            address1: r.address1 || '',
            address2: r.address2 || null,
            city: r.city || '',
            state: r.state || '',
            zip: r.zip || '',
          };
          if (r.note) row.gift_note = r.note;
          recipients.push(row);
        });
      });
      return recipients;
    }

    /* Basket prices are Shopify cents; API snapshot expects dollars. */
    buildOrderPayload(buyer) {
      const basket = this.readBasket().map((item) => ({
        variant_id: Number(item.variantId),
        quantity: Number(item.quantity) || 1,
        price: Math.round((Number(item.price) || 0)) / 100,
      }));

      const payload = {
        email: buyer.email,
        buyer_name: buyer.name,
        basket: basket,
        recipients: this.flattenRecipients(),
      };
      if (buyer.company) payload.company = buyer.company;
      if (buyer.phone) payload.buyer_phone = buyer.phone;
      if (buyer.logoUrl) payload.logo_url = buyer.logoUrl;
      const schedule = this.getScheduleFromForm();
      if (schedule.date) {
        payload.ship_date = schedule.date;
        payload.ship_date_kind = 'hold_until';
      }
      return payload;
    }

    /* Live destination sales tax (hardcoded per-state rate table on the API).
       Debounce edits and drop stale responses (a newer request may resolve
       before an older one). */
    scheduleTaxEstimate() {
      if (this.taxDebounce) clearTimeout(this.taxDebounce);
      this.taxDebounce = setTimeout(() => this.fetchTaxEstimate(), 800);
    }

    async fetchTaxEstimate() {
      if (!this.taxRowEl || !this.taxEl) return;

      const base = this.apiBase();
      const basket = this.readBasket();
      const recipients = this.flattenRecipients();

      if (!base || !basket.length || !recipients.length) {
        this.taxRowEl.hidden = true;
        this.taxEstimate = null;
        return;
      }

      const requestId = (this.taxRequestId = (this.taxRequestId || 0) + 1);

      try {
        const payload = {
          basket: basket.map((item) => ({
            variant_id: Number(item.variantId),
            quantity: Number(item.quantity) || 1,
            price: Math.round(Number(item.price) || 0) / 100,
          })),
          recipients: recipients,
        };

        const response = await fetch(base + '/api/corporate/tax-estimate', {
          method: 'POST',
          headers: this.apiHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(payload),
        });

        let body = null;
        try {
          body = await response.json();
        } catch (e) {
          body = null;
        }

        if (requestId !== this.taxRequestId) return; /* stale response */
        if (!response.ok || !body || !body.ok) {
          throw new Error((body && body.error) || 'tax_estimate_failed');
        }

        this.taxEstimate = body;
        this.taxEl.textContent = this.formatMoney(Number(body.tax_cents) || 0);
        this.taxRowEl.hidden = false;
      } catch (err) {
        if (requestId !== this.taxRequestId) return;
        /* Estimate is informational — hide the row rather than block anything.
           The authoritative amount is recomputed server-side at Pay Now. */
        console.error('[corporate-order-details] tax estimate failed:', err);
        this.taxRowEl.hidden = true;
        this.taxEstimate = null;
      }
    }

    cartRoutes() {
      const routes = (window.FoxTheme && FoxTheme.routes) || {};
      return {
        clear: '/cart/clear.js',
        add: routes.cart_add_url || '/cart/add.js',
        update: routes.cart_update_url || '/cart/update.js',
      };
    }

    async clearCart() {
      const response = await fetch(this.cartRoutes().clear, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('cart_clear_failed');
    }

    async addBasketToCart(token, buyer, tax) {
      const recipientCount = this.totalRecipientCount();
      const items = this.readBasket().map((item) => ({
        id: Number(item.variantId),
        quantity: (Number(item.quantity) || 1) * recipientCount,
      }));

      if (!items.length) throw new Error('empty_basket');

      /* Destination sales tax rides as one hidden-product "Sales Tax" line.
         The API signs token:cents (HMAC); the wildgrain-bundle Cart Transform
         function verifies the signature and reprices the line from $0.01 to
         the exact amount at checkout. Underscore-prefixed properties stay
         invisible to the buyer. */
      if (tax && tax.variant_id && Number(tax.cents) > 0 && tax.signature) {
        items.push({
          id: Number(tax.variant_id),
          quantity: 1,
          properties: {
            _corporate_order: token,
            _tax_cents: String(tax.cents),
            _tax_sig: tax.signature,
          },
        });
      }

      const addResponse = await fetch(this.cartRoutes().add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      });
      if (!addResponse.ok) {
        let detail = '';
        try {
          const errBody = await addResponse.json();
          detail = errBody.description || errBody.message || '';
        } catch (e) {
          /* ignore */
        }
        throw new Error(detail || 'cart_add_failed');
      }

      const attributes = { corporate_order_token: token };
      if (buyer.company) attributes.corporate_company = buyer.company;
      if (buyer.name) attributes.corporate_buyer_name = buyer.name;
      if (buyer.phone) attributes.corporate_buyer_phone = buyer.phone;
      const schedule = this.getScheduleFromForm();
      if (schedule.date) {
        attributes.corporate_ship_date = schedule.date;
        attributes.corporate_ship_date_kind = 'hold_until';
      }

      const updateResponse = await fetch(this.cartRoutes().update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
      });
      if (!updateResponse.ok) throw new Error('cart_attributes_failed');
    }

    shopBase() {
      return (
        this.dataset.shopUrl ||
        (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.shop_url) ||
        ''
      ).replace(/\/$/, '');
    }

    isOnShopHost() {
      const base = this.shopBase();
      if (!base) return true;
      try {
        return window.location.origin === new URL(base).origin;
      } catch (e) {
        return true;
      }
    }

    checkoutPath(email) {
      const params = new URLSearchParams();
      if (email) params.set('checkout[email]', email);
      /* Prefer standard checkout over Shop Pay (401s often in theme-dev proxy). */
      params.set('skip_shop_pay', 'true');
      if (this.dataset.themeId) params.set('preview_theme_id', this.dataset.themeId);
      const query = params.toString();
      return query ? '/checkout?' + query : '/checkout';
    }

    /**
     * Absolute cart lines for a permalink: /cart/VARIANT:QTY[,...]?...
     * Used after /cart/clear so repeat Pay Now does not stack on the old cart.
     */
    cartPermalinkPath(token, buyer) {
      const recipientCount = this.totalRecipientCount();
      const lineParts = this.readBasket()
        .map((item) => {
          const id = Number(item.variantId);
          const qty = (Number(item.quantity) || 1) * recipientCount;
          if (!Number.isFinite(id) || id <= 0 || !Number.isFinite(qty) || qty <= 0) return null;
          return id + ':' + qty;
        })
        .filter(Boolean);

      /* Cart permalinks cannot carry line item properties, so this theme-dev /
         off-shop-host fallback cannot add the signed "Sales Tax" line — tax
         is not collected on this path and the API webhook will HOLD the order
         if tax was due. Production buyers are on the shop host and use
         addBasketToCart. */
      const lines = lineParts.join(',');

      const params = new URLSearchParams();
      params.set('attributes[corporate_order_token]', token);
      if (buyer.company) params.set('attributes[corporate_company]', buyer.company);
      if (buyer.name) params.set('attributes[corporate_buyer_name]', buyer.name);
      if (buyer.phone) params.set('attributes[corporate_buyer_phone]', buyer.phone);
      const schedule = this.getScheduleFromForm();
      if (schedule.date) {
        params.set('attributes[corporate_ship_date]', schedule.date);
        params.set('attributes[corporate_ship_date_kind]', 'hold_until');
      }
      /* checkout[email] alone = Hash. Do NOT also set checkout=true (String) —
         Shopify then errors: "expected String to be a Hash: checkout". */
      if (buyer.email) params.set('checkout[email]', buyer.email);
      params.set('skip_shop_pay', 'true');
      if (this.dataset.themeId) params.set('preview_theme_id', this.dataset.themeId);

      return '/cart/' + lines + '?' + params.toString();
    }

    /**
     * Theme-dev (127.0.0.1:9292) cart cookies live on the shop host.
     * Cross-origin POST from localhost does not send SameSite=Lax cart cookies,
     * so POST /cart/clear was emptying a different empty cart while the real
     * cart kept stacking on the permalink add. Use a top-level GET clear
     * (cookies are sent) with return_to = cart permalink.
     */
    redirectToCheckout(token, buyer) {
      const returnTo = this.checkoutPath(buyer.email);

      if (this.isOnShopHost()) {
        window.location.href = returnTo;
        return;
      }

      const shop = this.shopBase();
      if (!shop) {
        window.location.href = returnTo;
        return;
      }

      const permalink = this.cartPermalinkPath(token, buyer);
      window.location.href =
        shop + '/cart/clear?return_to=' + encodeURIComponent(permalink);
    }

    async handlePayNow(event) {
      event.preventDefault();
      if (this.paying) return;

      this.validationAttempted = true;
      this.buyerNameTouched = true;
      this.buyerEmailTouched = true;
      this.buyerPhoneTouched = true;
      this.renderLists();
      this.refreshInlineValidation();
      this.updatePayNowState();

      const buyer = this.getBuyerFromForm();
      if (!this.isBuyerNameValid(buyer.name)) {
        this.showPayError(this.t('buyerNameRequired'));
        if (this.buyerNameInput) this.buyerNameInput.focus();
        return;
      }
      if (!this.isBuyerEmailValid(buyer.email)) {
        this.showPayError(this.t('buyerEmailRequired'));
        if (this.buyerEmailInput) this.buyerEmailInput.focus();
        return;
      }
      if (!this.isBuyerPhoneValid(buyer.phone)) {
        this.showPayError(this.t('buyerPhoneInvalid'));
        if (this.buyerPhoneInput) this.buyerPhoneInput.focus();
        return;
      }
      const schedule = this.getScheduleFromForm();
      const scheduleError = this.scheduleErrorMessage(schedule);
      if (scheduleError) {
        this.showPayError(scheduleError);
        if (this.shipErrorEl) {
          this.shipErrorEl.textContent = scheduleError;
          this.shipErrorEl.hidden = false;
        }
        if (this.shipDateInput) this.shipDateInput.focus();
        return;
      }
      if (!this.readBasket().length || !this.totalRecipientCount()) {
        this.updatePayNowState();
        return;
      }

      const base = this.apiBase();
      if (!base) {
        this.showPayError(this.t('apiMissing'));
        return;
      }

      const recipientError = this.validateAllRecipientsMessage();
      if (recipientError) {
        this.showPayError('');
        if (this.listErrorsEl && !this.listErrorsEl.hidden) {
          this.listErrorsEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        return;
      }

      this.showPayError('');
      this.writeBuyer(buyer);
      this.setPayLoading(true);

      try {
        const payload = this.buildOrderPayload(buyer);
        /* A second Pay Now updates the unpaid draft from the first click.
           The API ignores the token unless it is still an unpaid checkout
           draft, and answers with whichever token it actually saved. */
        const pending =
          window.WildgrainCorporateBasket && window.WildgrainCorporateBasket.readPending();
        if (pending && pending.token) payload.reuse_token = pending.token;
        const response = await fetch(base + '/api/corporate/orders', {
          method: 'POST',
          headers: this.apiHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(payload),
        });

        let body = null;
        try {
          body = await response.json();
        } catch (e) {
          body = null;
        }

        if (!response.ok || !body || !body.ok || !body.token) {
          const apiError = body && body.error;
          const message =
            apiError && apiError !== 'internal' && apiError !== 'rate_limited'
              ? this.humanizeApiError(apiError)
              : this.t('payError');
          throw new Error(message);
        }

        /* Remember the order across the checkout redirect. The basket stays
           put for now — checkout can still be abandoned, and the buyer would
           come back to an empty page. It is cleared on the next visit here or
           on the confirmation page, once the order is past draft. */
        if (window.WildgrainCorporateBasket) {
          window.WildgrainCorporateBasket.markPending(body.token);
        }

        if (this.isOnShopHost()) {
          await this.clearCart();
          await this.addBasketToCart(body.token, buyer, body.tax || null);
        }
        /* Off shop host (theme-dev): form POST builds cart on the real domain. */
        this.redirectToCheckout(body.token, buyer);
      } catch (err) {
        console.error('[corporate-order-details] Pay Now failed:', err);
        const message =
          err && err.message && err.message !== 'cart_clear_failed' && err.message !== 'cart_add_failed'
            ? err.message
            : this.t('payError');
        this.setPayLoading(false);

        /* Recipient problems belong on the list they came from, not under the
           button. The API reports every bad row in one string, so split it back
           into a bullet per recipient. Only reachable for rules the client
           doesn't mirror — anything it knows about is caught before this. */
        if (/^Recipient\s+\d/.test(message)) {
          this.showPayError('');
          this.showListErrors(message.split(/\s+(?=Recipient\s+\d)/));
          if (this.listErrorsEl) {
            this.listErrorsEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
          return;
        }

        this.showPayError(message);
      }
    }

    handleClick(event) {
      const stepBtn = event.target.closest('[data-cod-step][data-variant-id]');
      if (stepBtn) {
        this.changeQuantity(stepBtn.dataset.variantId, Number(stepBtn.dataset.codStep));
        return;
      }

      const removeBtn = event.target.closest('[data-cod-remove]');
      if (removeBtn) {
        this.writeBasket(this.readBasket().filter((item) => item.variantId !== removeBtn.dataset.variantId));
        return;
      }

      if (event.target.closest('[data-cod-logo-remove]')) {
        this.removeLogo();
        return;
      }

      if (event.target.closest('[data-cod-list-create]')) {
        this.openListModal();
        return;
      }

      const tokenBtn = event.target.closest('[data-cod-note-token]');
      if (tokenBtn) {
        this.insertNoteToken(tokenBtn);
        return;
      }

      const bulkApply = event.target.closest('[data-cod-bulk-note-apply]');
      if (bulkApply) {
        this.applyBulkNote(bulkApply.dataset.codBulkNoteApply);
        return;
      }

      if (event.target.closest('[data-cod-bulk-note-confirm-apply]')) {
        this.applyBulkNote('all', true);
        return;
      }

      if (event.target.closest('[data-cod-bulk-note-confirm-cancel]')) {
        this.hideBulkNoteConfirm();
        return;
      }

      const editRecipient = event.target.closest('[data-cod-recipient-edit]');
      if (editRecipient) {
        this.openRecipientEditor(editRecipient.dataset.listId, Number(editRecipient.dataset.recipientIndex));
        return;
      }

      const removeRecipient = event.target.closest('[data-cod-recipient-remove]');
      if (removeRecipient) {
        this.removeRecipient(removeRecipient.dataset.listId, Number(removeRecipient.dataset.recipientIndex));
        return;
      }

      const editList = event.target.closest('[data-cod-list-edit]');
      if (editList) {
        this.openListModal(editList.dataset.listId);
        return;
      }

      const removeList = event.target.closest('[data-cod-list-remove]');
      if (removeList) {
        this.writeLists(this.readLists().filter((list) => list.id !== removeList.dataset.listId));
        return;
      }

      if (event.target.closest('[data-cod-list-close]')) {
        this.closeListModal();
        return;
      }

      const methodCard = event.target.closest('[data-cod-method]');
      if (methodCard) {
        this.selectMethod(methodCard.dataset.codMethod);
        return;
      }

      if (event.target.closest('[data-cod-method-next]')) {
        this.goToMethodStep();
        return;
      }

      if (event.target.closest('[data-cod-step-back]')) {
        if (this.editingRecipientIndex != null) {
          this.closeListModal();
          return;
        }
        this.showModalStep('method');
        return;
      }

      if (event.target.closest('[data-cod-recipient-add]')) {
        this.addAnotherRecipient();
        return;
      }

      if (event.target.closest('[data-cod-list-save]')) {
        this.saveList();
      }
    }

    changeQuantity(variantId, delta) {
      const current = this.readBasket().find((item) => item.variantId === variantId);
      if (!current) return;
      this.setQuantity(variantId, (current.quantity || 1) + delta);
    }

    handleQuantityInput(event) {
      const input = event.target.closest('[data-cod-qty]');
      if (!input) return;
      this.setQuantity(input.dataset.variantId, Number(input.value));
    }

    setQuantity(variantId, quantity) {
      quantity = Math.max(1, Math.floor(quantity) || 1);
      const items = this.readBasket().map((item) => {
        if (item.variantId === variantId) item.quantity = quantity;
        return item;
      });
      this.writeBasket(items);
    }

    totalRecipientCount(lists) {
      return (lists || this.readLists()).reduce(
        (sum, list) => sum + (Array.isArray(list.recipients) ? list.recipients.length : 0),
        0
      );
    }

    perRecipientSubtotal(items) {
      return (items || this.readBasket()).reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
    }

    renderItems(items) {
      if (!this.itemsEl) return;
      items = items || this.readBasket();

      const recipientCount = this.totalRecipientCount();
      const multiplier = Math.max(recipientCount, 1);
      const perRecipient = this.perRecipientSubtotal(items);
      const subtotal = perRecipient * multiplier;

      if (this.emptyEl) this.emptyEl.hidden = items.length > 0;
      if (this.summaryEl) this.summaryEl.hidden = items.length === 0;
      if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(subtotal);
      if (this.subtotalLabelEl) {
        if (recipientCount === 1) {
          this.subtotalLabelEl.textContent = this.t('subtotalOneRecipient');
        } else if (recipientCount > 1) {
          this.subtotalLabelEl.textContent = this.t('subtotalRecipients').replace('__COUNT__', String(recipientCount));
        } else {
          this.subtotalLabelEl.textContent = 'Subtotal';
        }
      }

      this.itemsEl.textContent = '';

      items.forEach((item) => {
        const quantity = item.quantity || 1;
        const totalQty = quantity * multiplier;

        const row = document.createElement('div');
        row.className = 'cod-item';

        const media = document.createElement('div');
        media.className = 'cod-item__media';

        if (item.image) {
          const img = document.createElement('img');
          img.className = 'cod-item__image';
          img.src = item.image;
          img.alt = item.title;
          img.loading = 'lazy';
          media.appendChild(img);
        }

        const badge = document.createElement('span');
        badge.className = 'cod-item__badge';
        badge.textContent = String(totalQty);
        media.appendChild(badge);
        row.appendChild(media);

        const info = document.createElement('div');
        info.className = 'cod-item__info';

        const title = document.createElement('p');
        title.className = 'cod-item__title';
        title.textContent = item.title;
        info.appendChild(title);

        if (item.variantTitle) {
          const variant = document.createElement('p');
          variant.className = 'cod-item__variant';
          variant.textContent = item.variantTitle;
          info.appendChild(variant);
        }

        const qtyHint = document.createElement('p');
        qtyHint.className = 'cod-item__variant';
        qtyHint.textContent = quantity + ' ' + this.t('perRecipient');
        info.appendChild(qtyHint);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cod-item__remove';
        remove.textContent = this.t('remove');
        remove.dataset.codRemove = '';
        remove.dataset.variantId = item.variantId;
        info.appendChild(remove);

        row.appendChild(info);

        const controls = document.createElement('div');
        controls.className = 'cod-item__controls';

        const stepper = document.createElement('div');
        stepper.className = 'cod-stepper';

        const minus = document.createElement('button');
        minus.type = 'button';
        minus.className = 'cod-stepper__btn';
        minus.textContent = '\u2212';
        minus.setAttribute('aria-label', this.t('decrease'));
        minus.dataset.codStep = '-1';
        minus.dataset.variantId = item.variantId;
        minus.disabled = quantity <= 1;

        const value = document.createElement('input');
        value.type = 'number';
        value.className = 'cod-stepper__value';
        value.min = '1';
        value.inputMode = 'numeric';
        value.value = quantity;
        value.setAttribute('aria-label', this.t('quantity') + ' \u2014 ' + item.title);
        value.dataset.codQty = '';
        value.dataset.variantId = item.variantId;

        const plus = document.createElement('button');
        plus.type = 'button';
        plus.className = 'cod-stepper__btn';
        plus.textContent = '+';
        plus.setAttribute('aria-label', this.t('increase'));
        plus.dataset.codStep = '1';
        plus.dataset.variantId = item.variantId;

        stepper.append(minus, value, plus);
        controls.appendChild(stepper);

        const linePrice = document.createElement('span');
        linePrice.className = 'cod-item__price';
        linePrice.textContent = this.formatMoney(item.price * quantity * multiplier);
        controls.appendChild(linePrice);

        row.appendChild(controls);
        this.itemsEl.appendChild(row);
      });
    }

    /* Recipient lists — quick entry form or CSV upload, stored locally until payment. */

    readLists() {
      try {
        const lists = JSON.parse(localStorage.getItem(LISTS_KEY));
        return Array.isArray(lists) ? lists : [];
      } catch (e) {
        return [];
      }
    }

    writeLists(lists) {
      try {
        localStorage.setItem(LISTS_KEY, JSON.stringify(lists));
      } catch (e) {
        /* storage unavailable */
      }
      this.renderLists(lists);
      this.renderItems();
      this.refreshInlineValidation();
      this.updatePayNowState();
      this.scheduleTaxEstimate();
    }

    renderLists(lists) {
      if (!this.listsEl) return;
      lists = lists || this.readLists();
      this.listsEl.textContent = '';

      const basket = this.readBasket();
      const perRecipientTotal = this.perRecipientSubtotal(basket);
      let globalIndex = 0;
      const highlightRecipients = this.shouldHighlightRecipientErrors();

      lists.forEach((list) => {
        const recipients = Array.isArray(list.recipients) ? list.recipients : [];
        const block = document.createElement('div');
        block.className = 'cod-list-block';

        const header = document.createElement('div');
        header.className = 'cod-list-package';

        const thumbs = document.createElement('div');
        thumbs.className = 'cod-list-package__thumbs';

        basket.forEach((item) => {
          const thumb = document.createElement('div');
          thumb.className = 'cod-list-package__thumb';
          if (item.image) {
            const img = document.createElement('img');
            img.src = item.image;
            img.alt = item.title;
            img.loading = 'lazy';
            thumb.appendChild(img);
          }
          const badge = document.createElement('span');
          badge.className = 'cod-list-package__badge';
          badge.textContent = String(item.quantity || 1);
          thumb.appendChild(badge);
          thumbs.appendChild(thumb);
        });

        const perLabel = document.createElement('span');
        perLabel.className = 'cod-list-package__label';
        perLabel.textContent = this.t('perRecipient');

        const removeList = document.createElement('button');
        removeList.type = 'button';
        removeList.className = 'cod-list-package__close';
        removeList.setAttribute('aria-label', this.t('remove'));
        removeList.dataset.codListRemove = '';
        removeList.dataset.listId = list.id;
        removeList.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';

        header.append(thumbs, perLabel, removeList);
        block.appendChild(header);

        const rows = document.createElement('div');
        rows.className = 'cod-recipients';

        recipients.forEach((recipient, index) => {
          const validation = this.getRecipientValidationEntry(recipient, globalIndex);
          rows.appendChild(
            this.buildRecipientRow(list.id, recipient, index, perRecipientTotal, {
              displayIndex: globalIndex + 1,
              highlight: highlightRecipients && !validation.valid,
              message: validation.message,
            })
          );
          globalIndex += 1;
        });

        block.appendChild(rows);
        this.listsEl.appendChild(block);
      });

      this.refreshBuyerFieldValidation();
      this.refreshBulkNoteVisibility(lists);
      this.updateValidationSummary();
    }

    buildRecipientRow(listId, recipient, index, perRecipientTotal, validationState) {
      const row = document.createElement('div');
      row.className = 'cod-recipient';
      const displayIndex =
        validationState && validationState.displayIndex != null ? validationState.displayIndex : index + 1;

      if (validationState && validationState.highlight) {
        row.classList.add('cod-recipient--error');
        row.setAttribute('aria-invalid', 'true');
      }

      const indexEl = document.createElement('span');
      indexEl.className = 'cod-recipient__index';
      indexEl.textContent = String(displayIndex);

      const details = document.createElement('div');
      details.className = 'cod-recipient__details';

      const name = document.createElement('p');
      name.className = 'cod-recipient__name';
      name.textContent = [recipient.firstName, recipient.lastName].filter(Boolean).join(' ');
      details.appendChild(name);

      if (recipient.email) {
        const email = document.createElement('p');
        email.className = 'cod-recipient__address';
        email.textContent = recipient.email;
        details.appendChild(email);
      }

      if (recipient.phone) {
        const phone = document.createElement('p');
        phone.className = 'cod-recipient__address';
        phone.textContent = recipient.phone;
        details.appendChild(phone);
      }

      this.formatAddressLines(recipient).forEach((line) => {
        const addressLine = document.createElement('p');
        addressLine.className = 'cod-recipient__address';
        addressLine.textContent = line;
        details.appendChild(addressLine);
      });

      if (recipient.note) {
        /* Shows the note as it will print, not the template. */
        const note = document.createElement('p');
        note.className = 'cod-recipient__note';
        note.textContent = '"' + renderGiftNote(recipient.note, recipient) + '"';
        if (giftNoteHasTokens(recipient.note)) note.title = recipient.note;
        details.appendChild(note);
      }

      const price = document.createElement('span');
      price.className = 'cod-recipient__price';
      price.textContent = this.formatMoney(perRecipientTotal);

      const actions = document.createElement('div');
      actions.className = 'cod-recipient__actions';

      const edit = document.createElement('button');
      edit.type = 'button';
      edit.className = 'cod-recipient__edit';
      edit.textContent = this.t('edit');
      edit.dataset.codRecipientEdit = '';
      edit.dataset.listId = listId;
      edit.dataset.recipientIndex = String(index);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cod-recipient__remove';
      remove.setAttribute('aria-label', this.t('removeRecipient'));
      remove.dataset.codRecipientRemove = '';
      remove.dataset.listId = listId;
      remove.dataset.recipientIndex = String(index);
      remove.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';

      actions.append(edit, remove);
      row.append(indexEl, details, price, actions);
      return row;
    }

    formatAddressLines(recipient) {
      const lines = [];
      if (recipient.address1) lines.push(recipient.address1);
      if (recipient.address2) lines.push(recipient.address2);

      const region = [recipient.state, recipient.zip].filter(Boolean).join(' ');
      const cityLine = [recipient.city, region].filter(Boolean).join(', ');
      if (cityLine) lines.push(cityLine);
      return lines;
    }

    formatCountry(code) {
      if (!code) return '';
      const normalized = String(code).trim().toUpperCase();
      if (normalized === 'US' || normalized === 'USA') return this.t('countryUs');
      if (normalized === 'CA' || normalized === 'CAN') return this.t('countryCa');
      return code;
    }

    recipientCountLabel(count) {
      return count + ' ' + (count === 1 ? this.t('recipient') : this.t('recipients'));
    }

    removeRecipient(listId, index) {
      const lists = this.readLists()
        .map((list) => {
          if (list.id !== listId) return list;
          const recipients = (list.recipients || []).filter((_, i) => i !== index);
          return Object.assign({}, list, { recipients: recipients });
        })
        .filter((list) => list.recipients && list.recipients.length);
      this.writeLists(lists);
    }

    openRecipientEditor(listId, index) {
      if (!this.listModal) return;
      const list = this.readLists().find((entry) => entry.id === listId);
      if (!list || !list.recipients || !list.recipients[index]) return;

      this.editingListId = listId;
      this.editingRecipientIndex = index;
      this.draftRecipients = list.recipients.slice();
      this.draftListName = list.name || '';

      this.showListError('');
      this.showCsvError('');
      this.selectMethod('manual');
      this.fillRecipientForm(list.recipients[index]);
      this.showModalStep('manual');

      this.listModal.removeAttribute('hidden');
      document.documentElement.classList.add('cg-modal-open');
      document.addEventListener('keydown', this.handleModalKeydown);
    }

    fillRecipientForm(recipient) {
      if (!this.recipientForm || !recipient) return;
      const elements = this.recipientForm.elements;
      if (elements.firstName) elements.firstName.value = recipient.firstName || '';
      if (elements.lastName) elements.lastName.value = recipient.lastName || '';
      if (elements.email) elements.email.value = recipient.email || '';
      if (elements.phone) elements.phone.value = recipient.phone || '';
      if (elements.address1) elements.address1.value = recipient.address1 || '';
      if (elements.address2) elements.address2.value = recipient.address2 || '';
      if (elements.city) elements.city.value = recipient.city || '';
      /* CSV rows may say "Hawaii"; the <select> values are codes. */
      if (elements.state) elements.state.value = normalizeStateCode(recipient.state);
      if (elements.zip) elements.zip.value = recipient.zip || '';
      if (this.recipientNote) {
        this.recipientNote.value = this.sanitizeRecipientNote(recipient.note || '');
        this.updateRecipientNoteUi();
      }
      this.refreshRecipientStateWarning();
    }

    openListModal(listId) {
      if (!this.listModal) return;

      /* Re-opening "Add recipient" should append to the existing list, not
         create a second package block (same basket, cumulative recipients). */
      if (!listId) {
        const existingLists = this.readLists();
        if (existingLists.length > 0) listId = existingLists[0].id;
      }

      this.editingListId = listId || null;
      this.editingRecipientIndex = null;
      const list = listId ? this.readLists().find((entry) => entry.id === listId) : null;
      this.draftRecipients = list && Array.isArray(list.recipients) ? list.recipients.slice() : [];
      this.draftListName = list ? list.name : '';

      this.showListError('');
      this.showCsvError('');
      this.resetRecipientForm();
      this.selectMethod('manual');
      if (listId && list) {
        this.showModalStep('manual');
      } else {
        this.showModalStep('method');
      }

      this.listModal.removeAttribute('hidden');
      document.documentElement.classList.add('cg-modal-open');
      document.addEventListener('keydown', this.handleModalKeydown);
    }

    closeListModal() {
      if (!this.listModal || this.listModal.hidden) return;
      this.listModal.setAttribute('hidden', '');
      document.documentElement.classList.remove('cg-modal-open');
      document.removeEventListener('keydown', this.handleModalKeydown);
      this.editingListId = null;
      this.editingRecipientIndex = null;
      this.draftRecipients = [];
      this.draftListName = '';
    }

    selectMethod(method) {
      this.selectedMethod = method === 'csv' ? 'csv' : 'manual';
      this.methodCards.forEach((card) => {
        const selected = card.dataset.codMethod === this.selectedMethod;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
    }

    showModalStep(step) {
      Object.keys(this.stepEls).forEach((key) => {
        const el = this.stepEls[key];
        if (!el) return;
        if (key === step) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });

      if (!this.listModalTitle) return;
      if (step === 'method') {
        this.listModalTitle.textContent = this.t('enterRecipients');
      } else if (step === 'csv') {
        this.listModalTitle.textContent = this.t('uploadRecipients');
      } else {
        this.listModalTitle.textContent = this.draftRecipients.length
          ? this.t('addRecipientTitle')
          : this.t('addFirstRecipient');
      }
    }

    goToMethodStep() {
      if (this.selectedMethod === 'csv') {
        this.showCsvError('');
        this.showModalStep('csv');
        return;
      }
      this.showListError('');
      this.showModalStep('manual');
      const first = this.recipientForm && this.recipientForm.elements.firstName;
      if (first) first.focus();
    }

    handleRecipientFormInput(event) {
      if (event.target && event.target.classList) {
        event.target.classList.remove('cod-input--error');
        event.target.removeAttribute('aria-invalid');
      }
      if (event.target && event.target.name === 'state') this.refreshRecipientStateWarning();
      /* Runs for every field, not just the note: typing a name changes what
         the note's tokens resolve to, so the preview has to follow along. */
      this.updateRecipientNoteUi();
    }

    /* Inline "we do not ship there" under the state dropdown. Shown live as
       the state changes and again on save; cleared with the other field errors. */
    refreshRecipientStateWarning() {
      if (!this.recipientForm) return;
      const select = this.recipientForm.elements.state;
      const stateName = select ? nonShippableStateName(select.value) : '';
      this.showRecipientStateWarning(stateName);
      if (select && stateName) {
        select.classList.add('cod-input--error');
        select.setAttribute('aria-invalid', 'true');
      }
    }

    showRecipientStateWarning(stateName) {
      if (!this.recipientStateErrorEl) return;
      const message = stateName ? this.t('recipientStateUnshippable').replace('__STATE__', stateName) : '';
      this.recipientStateErrorEl.textContent = message;
      this.recipientStateErrorEl.hidden = !message;
    }

    /* Gift note tokens -------------------------------------------------- */

    noteTokenTarget(bar) {
      return bar && bar.dataset.codNoteTokens === 'bulk' ? this.bulkNoteInput : this.recipientNote;
    }

    insertNoteToken(button) {
      const target = this.noteTokenTarget(button.closest('[data-cod-note-tokens]'));
      if (!target) return;

      const token = '{{' + button.dataset.codNoteToken + '}}';
      const current = String(target.value || '');
      const hasCaret = typeof target.selectionStart === 'number';
      const start = hasCaret ? target.selectionStart : current.length;
      const end = hasCaret ? target.selectionEnd : start;
      const next = insertGiftNoteToken(current, token, start, end);

      /* Names inserted via tokens count as 14 each toward 240. Extra spaces
         around a token still do, so a full message can still block an insert. */
      if (giftNoteMessageLength(next.value) > RECIPIENT_NOTE_MAX) {
        const message = this.t('giftNoteTokenNoRoom')
          .replace('__TOKEN__', token)
          .replace('__MAX__', String(RECIPIENT_NOTE_MAX));
        if (target === this.bulkNoteInput) this.showBulkNoteStatus(message, true);
        else this.showListError(message);
        return;
      }

      target.value = next.value;
      target.focus();
      if (hasCaret) target.setSelectionRange(next.caret, next.caret);

      if (target === this.bulkNoteInput) this.handleBulkNoteInput();
      else this.updateRecipientNoteUi();
    }

    /* Counter includes the typed message. {{first_name}} / {{last_name}} each count as 14. */
    updateNoteCounter(counterEl, raw) {
      const used = giftNoteMessageLength(raw);
      if (counterEl) {
        counterEl.textContent = String(used);
        const wrap = counterEl.closest('.cod-recipient-note-count');
        if (wrap) wrap.classList.toggle('cod-recipient-note-count--limit', used >= RECIPIENT_NOTE_MAX);
      }
    }

    renderNotePreview(el, raw, recipient) {
      if (!el) return;
      if (!giftNoteHasTokens(raw)) {
        el.hidden = true;
        el.textContent = '';
        return;
      }
      const named = [recipient && recipient.firstName, recipient && recipient.lastName]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
      const subject = named ? recipient : GIFT_NOTE_PREVIEW_SAMPLE;
      const rendered = renderGiftNote(raw, subject);
      el.hidden = false;
      el.textContent = this.t('giftNotePreview')
        .replace('__WHO__', named || this.t('giftNotePreviewSample'))
        .replace('__NOTE__', rendered || this.t('giftNotePreviewEmpty'));
    }

    updateRecipientNoteUi() {
      if (!this.recipientNote) return;
      const cleaned = this.sanitizeRecipientNote(this.recipientNote.value);
      if (cleaned !== this.recipientNote.value) this.recipientNote.value = cleaned;

      const elements = this.recipientForm ? this.recipientForm.elements : null;
      const recipient = {
        firstName: elements && elements.firstName ? elements.firstName.value : '',
        lastName: elements && elements.lastName ? elements.lastName.value : '',
      };

      this.updateNoteCounter(this.recipientNoteCounter, this.recipientNote.value);
      this.renderNotePreview(this.recipientNotePreview, this.recipientNote.value, recipient);
    }

    /* Bulk gift note ---------------------------------------------------- */

    showBulkNoteStatus(message, isError) {
      if (!this.bulkNoteStatus) return;
      this.bulkNoteStatus.textContent = message || '';
      this.bulkNoteStatus.hidden = !message;
      this.bulkNoteStatus.classList.toggle('cod-modal__error', Boolean(isError));
    }

    /* Preview the bulk note against a real name from the list when there is
       one, so what's on screen is what will actually print. */
    bulkNotePreviewRecipient() {
      let hit = null;
      this.readLists().forEach((list) => {
        (list.recipients || []).forEach((recipient) => {
          if (hit) return;
          if (String(recipient.firstName || '').trim() || String(recipient.lastName || '').trim()) {
            hit = recipient;
          }
        });
      });
      return hit ? { firstName: hit.firstName, lastName: hit.lastName } : { firstName: '', lastName: '' };
    }

    refreshBulkNotePreview() {
      if (!this.bulkNoteInput) return;
      const cleaned = this.sanitizeRecipientNote(this.bulkNoteInput.value);
      if (cleaned !== this.bulkNoteInput.value) this.bulkNoteInput.value = cleaned;

      const subject = this.bulkNotePreviewRecipient();
      this.updateNoteCounter(this.bulkNoteCounter, this.bulkNoteInput.value);
      this.renderNotePreview(this.bulkNotePreview, this.bulkNoteInput.value, subject);
    }

    handleBulkNoteInput() {
      this.refreshBulkNotePreview();
      this.showBulkNoteStatus('');
      this.hideBulkNoteConfirm();
    }

    hideBulkNoteConfirm() {
      if (this.bulkNoteConfirm) this.bulkNoteConfirm.hidden = true;
    }

    /* Names the notes that would be lost, rather than leaving the buyer to
       trust a bare count in a browser dialog. */
    showBulkNoteConfirm(losing) {
      if (!this.bulkNoteConfirm) return;
      const count = losing.length;
      const one = count === 1;

      if (this.bulkNoteConfirmTitle) {
        this.bulkNoteConfirmTitle.textContent = one
          ? this.t('giftNoteReplaceTitleOne')
          : this.t('giftNoteReplaceTitle').replace('__COUNT__', String(count));
      }
      if (this.bulkNoteConfirmApply) {
        this.bulkNoteConfirmApply.textContent = one
          ? this.t('giftNoteReplaceApplyOne')
          : this.t('giftNoteReplaceApply').replace('__COUNT__', String(count));
      }

      if (this.bulkNoteConfirmList) {
        this.bulkNoteConfirmList.textContent = '';
        losing.slice(0, 3).forEach((recipient) => {
          const item = document.createElement('li');
          const who = document.createElement('span');
          who.className = 'cod-bulk-note__confirm-who';
          who.textContent = [recipient.firstName, recipient.lastName].filter(Boolean).join(' ');
          const note = document.createElement('span');
          note.className = 'cod-bulk-note__confirm-note';
          note.textContent = '“' + renderGiftNote(recipient.note, recipient) + '”';
          item.append(who, document.createTextNode(' — '), note);
          this.bulkNoteConfirmList.appendChild(item);
        });
        if (count > 3) {
          const more = document.createElement('li');
          more.className = 'cod-bulk-note__confirm-more';
          more.textContent = this.t('giftNoteReplaceMore').replace('__COUNT__', String(count - 3));
          this.bulkNoteConfirmList.appendChild(more);
        }
      }

      this.bulkNoteConfirm.hidden = false;
    }

    refreshBulkNoteVisibility(lists) {
      if (!this.bulkNoteEl) return;
      this.bulkNoteEl.hidden = this.totalRecipientCount(lists) === 0;
      if (!this.bulkNoteEl.hidden) this.refreshBulkNotePreview();
    }

    validateGiftNoteText(raw) {
      const note = this.sanitizeRecipientNote(raw).trim();
      if (!note) return { ok: true, note: '' };
      const unknown = giftNoteUnknownTokens(note);
      if (unknown.length) {
        return {
          ok: false,
          error: this.t('giftNoteUnknownToken')
            .replace('__TOKEN__', '{{' + unknown[0] + '}}')
            .replace('__ALLOWED__', GIFT_NOTE_TOKENS.map((t) => '{{' + t + '}}').join(' or ')),
        };
      }
      if (giftNoteMessageLength(note) > RECIPIENT_NOTE_MAX) {
        return {
          ok: false,
          error: this.t('giftNoteTooLongFor').replace('__MAX__', String(RECIPIENT_NOTE_MAX)),
        };
      }
      return { ok: true, note: note };
    }

    /* mode 'empty' leaves existing notes alone; mode 'all' overwrites them,
       which is worth having now that one templated note reads personally to
       everyone — but it destroys hand-written notes, so it confirms first. */
    applyBulkNote(mode, confirmed) {
      if (!this.bulkNoteInput) return;

      const checked = this.validateGiftNoteText(this.bulkNoteInput.value);
      if (!checked.ok) {
        this.showBulkNoteStatus(checked.error, true);
        return;
      }
      if (!checked.note) {
        this.showBulkNoteStatus(this.t('giftNoteEmpty'), true);
        return;
      }

      const lists = this.readLists();
      const shouldApply = (recipient) =>
        mode === 'all' || !String(recipient.note || '').trim();

      let targets = 0;
      const losing = [];
      lists.forEach((list) => {
        (list.recipients || []).forEach((recipient) => {
          if (!shouldApply(recipient)) return;
          targets += 1;
          if (String(recipient.note || '').trim()) losing.push(recipient);
        });
      });

      if (!targets) {
        this.showBulkNoteStatus(
          mode === 'all' ? this.t('giftNoteNoRecipients') : this.t('giftNoteAllHaveNotes'),
          true
        );
        return;
      }

      if (mode === 'all' && losing.length && !confirmed) {
        this.showBulkNoteConfirm(losing);
        return;
      }

      lists.forEach((list) => {
        (list.recipients || []).forEach((recipient) => {
          if (shouldApply(recipient)) recipient.note = checked.note;
        });
      });

      this.hideBulkNoteConfirm();
      this.writeLists(lists);
      this.showBulkNoteStatus(
        targets === 1
          ? this.t('giftNoteAppliedOne')
          : this.t('giftNoteApplied').replace('__COUNT__', String(targets))
      );
    }

    /* Gift notes are capped at 240 characters. {{first_name}} / {{last_name}}
       each count as 14, and emoji is stripped. */
    sanitizeRecipientNote(value) {
      let note = String(value || '').replace(
        /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/gu,
        ''
      );
      while (note.length && giftNoteMessageLength(note) > RECIPIENT_NOTE_MAX) {
        note = note.slice(0, -1);
      }
      return note;
    }

    resetRecipientForm() {
      if (!this.recipientForm) return;
      this.recipientForm.reset();
      this.updateRecipientNoteUi();
      this.clearRecipientFieldErrors();
      this.showListError('');
    }

    readRecipientForm() {
      if (!this.recipientForm) return null;
      const elements = this.recipientForm.elements;
      const recipient = {
        firstName: (elements.firstName.value || '').trim(),
        lastName: (elements.lastName.value || '').trim(),
        email: (elements.email.value || '').trim(),
        phone: (elements.phone.value || '').trim(),
        address1: (elements.address1.value || '').trim(),
        address2: (elements.address2.value || '').trim(),
        city: (elements.city.value || '').trim(),
        state: (elements.state.value || '').trim(),
        zip: (elements.zip.value || '').trim(),
        country: 'US',
      };

      if (this.recipientNote) {
        const note = this.sanitizeRecipientNote(this.recipientNote.value).trim();
        if (note) recipient.note = note;
      }

      return recipient;
    }

    recipientWho(recipient, index) {
      const name = [recipient && recipient.firstName, recipient && recipient.lastName]
        .filter(Boolean)
        .join(' ');
      return name ? 'Recipient ' + (index + 1) + ' (' + name + ')' : 'Recipient ' + (index + 1);
    }

    joinFieldLabels(fields) {
      const labels = fields.map((field) => RECIPIENT_FIELD_LABELS[field] || field);
      if (labels.length === 1) return labels[0];
      if (labels.length === 2) return labels[0] + ' and ' + labels[1];
      return labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1];
    }

    recipientIssues(recipient) {
      const missing = REQUIRED_RECIPIENT_FIELDS.filter((field) => !(recipient && recipient[field]));
      const issues = [];
      if (missing.length) issues.push({ kind: 'missing', fields: missing });
      if (recipient && recipient.email && !EMAIL_REGEX.test(recipient.email)) {
        issues.push({ kind: 'invalid_email', fields: ['email'] });
      }
      if (recipient && recipient.phone && !isValidUsPhone(recipient.phone)) {
        issues.push({ kind: 'invalid_phone', fields: ['phone'] });
      }
      if (recipient && recipient.state && nonShippableStateName(recipient.state)) {
        issues.push({ kind: 'unshippable_state', fields: ['state'] });
      }
      /* The API rejects unsupported tokens, and rejecting here means catching
         it before the buyer pays rather than after. */
      if (recipient && recipient.note && giftNoteUnknownTokens(recipient.note).length) {
        issues.push({ kind: 'invalid_gift_note', fields: ['note'] });
      }
      return issues;
    }

    formatRecipientIssue(recipient, index, issue) {
      const who = this.recipientWho(recipient, index);
      if (issue.kind === 'invalid_email') {
        return this.t('recipientInvalidEmailNamed').replace('__WHO__', who);
      }
      if (issue.kind === 'invalid_phone') {
        return this.t('recipientInvalidPhoneNamed').replace('__WHO__', who);
      }
      if (issue.kind === 'unshippable_state') {
        return this.t('recipientUnshippableStateNamed')
          .replace('__WHO__', who)
          .replace('__STATE__', nonShippableStateName(recipient.state));
      }
      if (issue.kind === 'invalid_gift_note') {
        const unknown = giftNoteUnknownTokens(recipient.note);
        return this.t('giftNoteUnknownTokenNamed')
          .replace('__WHO__', who)
          .replace('__TOKEN__', '{{' + unknown[0] + '}}')
          .replace('__ALLOWED__', GIFT_NOTE_TOKENS.map((t) => '{{' + t + '}}').join(' or '));
      }
      return this.t('recipientMissing')
        .replace('__WHO__', who)
        .replace('__FIELDS__', this.joinFieldLabels(issue.fields || []));
    }

    formatIssueList(messages, extraLine) {
      if (!messages.length) return '';
      const shown = messages.slice(0, 4);
      const extra = messages.length - shown.length;
      if (extra > 0) {
        shown.push(this.t('csvMoreErrors').replace('__COUNT__', String(extra)));
      }
      if (extraLine) shown.push(extraLine);
      return shown.join(' ');
    }

    validateAllRecipientsMessage() {
      const messages = [];
      let index = 0;
      this.readLists().forEach((list) => {
        (list.recipients || []).forEach((recipient) => {
          this.recipientIssues(recipient).forEach((issue) => {
            messages.push(this.formatRecipientIssue(recipient, index, issue));
          });
          index += 1;
        });
      });
      return this.formatIssueList(messages);
    }

    humanizeApiError(error) {
      const text = String(error || '');
      const match = text.match(/^recipients\[(\d+)\]\.(\w+)\s+is\s+(required|invalid)/i);
      if (!match) return text;
      const index = Number(match[1]);
      const fieldKey = match[2];
      const kind = match[3].toLowerCase();
      const camel =
        fieldKey === 'first_name'
          ? 'firstName'
          : fieldKey === 'last_name'
            ? 'lastName'
            : fieldKey === 'address1'
              ? 'address1'
              : fieldKey;
      const rows = this.flattenRecipients();
      const raw = rows[index] || {};
      const recipient = {
        firstName: raw.first_name,
        lastName: raw.last_name,
      };
      if (kind === 'invalid' && (fieldKey === 'email' || camel === 'email')) {
        return this.formatRecipientIssue(recipient, index, { kind: 'invalid_email' });
      }
      return this.formatRecipientIssue(recipient, index, { kind: 'missing', fields: [camel] });
    }

    clearRecipientFieldErrors() {
      if (!this.recipientForm) return;
      this.recipientForm.querySelectorAll('.cod-input--error').forEach((el) => {
        el.classList.remove('cod-input--error');
        el.removeAttribute('aria-invalid');
      });
      this.showRecipientStateWarning('');
    }

    markRecipientFieldErrors(fields) {
      this.clearRecipientFieldErrors();
      if (!this.recipientForm || !fields || !fields.length) return;
      const elements = this.recipientForm.elements;
      let first = null;
      fields.forEach((name) => {
        const el = elements[name];
        if (!el) return;
        el.classList.add('cod-input--error');
        el.setAttribute('aria-invalid', 'true');
        if (!first) first = el;
      });
      if (first && typeof first.focus === 'function') first.focus();
    }

    showRecipientFormIssues(recipient) {
      const issues = this.recipientIssues(recipient);
      if (!issues.length) {
        this.clearRecipientFieldErrors();
        this.showListError('');
        return false;
      }
      const fields = [];
      issues.forEach((issue) => {
        (issue.fields || []).forEach((field) => {
          if (fields.indexOf(field) === -1) fields.push(field);
        });
      });
      this.markRecipientFieldErrors(fields);
      if (issues.some((issue) => issue.kind === 'unshippable_state')) {
        this.showRecipientStateWarning(nonShippableStateName(recipient.state));
      }
      const index =
        this.editingRecipientIndex != null
          ? this.editingRecipientIndex
          : this.draftRecipients.length;
      this.showListError(
        issues.map((issue) => this.formatRecipientIssue(recipient, index, issue)).join(' ')
      );
      return true;
    }

    isRecipientComplete(recipient) {
      return this.recipientIssues(recipient).length === 0;
    }

    isRecipientBlank(recipient) {
      if (!recipient) return true;
      return !(
        recipient.firstName ||
        recipient.lastName ||
        recipient.email ||
        recipient.phone ||
        recipient.address1 ||
        recipient.address2 ||
        recipient.city ||
        recipient.state ||
        recipient.zip ||
        recipient.note
      );
    }

    pushCurrentRecipient() {
      const recipient = this.readRecipientForm();
      if (this.isRecipientBlank(recipient)) return { ok: true, blank: true };
      if (this.showRecipientFormIssues(recipient)) {
        return { ok: false };
      }

      if (this.editingRecipientIndex != null) {
        this.draftRecipients[this.editingRecipientIndex] = recipient;
        return { ok: true, blank: false, updated: true };
      }

      if (this.draftRecipients.length >= MAX_RECIPIENTS) {
        this.showListError(this.t('listEmpty'));
        return { ok: false };
      }
      this.draftRecipients.push(recipient);
      return { ok: true, blank: false };
    }

    addAnotherRecipient() {
      if (this.editingRecipientIndex != null) {
        const result = this.pushCurrentRecipient();
        if (!result.ok) return;
        this.editingRecipientIndex = null;
        this.resetRecipientForm();
        this.showModalStep('manual');
        const first = this.recipientForm && this.recipientForm.elements.firstName;
        if (first) first.focus();
        return;
      }

      const result = this.pushCurrentRecipient();
      if (!result.ok) return;
      if (result.blank) {
        this.showRecipientFormIssues(this.readRecipientForm());
        return;
      }
      this.resetRecipientForm();
      this.showModalStep('manual');
      const first = this.recipientForm && this.recipientForm.elements.firstName;
      if (first) first.focus();
    }

    saveList() {
      const result = this.pushCurrentRecipient();
      if (!result.ok) return;
      /* Blank form is fine when saving a list that already has recipients
         (e.g. after "Add another"). Editing a single row requires a filled form. */
      if (result.blank && this.editingRecipientIndex != null) {
        this.showRecipientFormIssues(this.readRecipientForm());
        return;
      }

      if (!this.draftRecipients.length) {
        this.showListError(this.t('listEmpty'));
        return;
      }

      this.persistDraftList();
      this.closeListModal();
    }

    persistDraftList(preferredName) {
      const lists = this.readLists();
      const name =
        preferredName ||
        this.draftListName ||
        this.t('list') + ' ' + (lists.length + (this.editingListId ? 0 : 1));

      const existing = this.editingListId && lists.find((entry) => entry.id === this.editingListId);
      if (existing) {
        existing.name = name;
        existing.recipients = this.draftRecipients.slice(0, MAX_RECIPIENTS);
      } else {
        lists.push({
          id: Date.now().toString(36),
          name: name,
          recipients: this.draftRecipients.slice(0, MAX_RECIPIENTS),
        });
      }

      this.writeLists(lists);
    }

    showListError(message) {
      if (!this.listErrorEl) return;
      this.listErrorEl.textContent = message;
      this.listErrorEl.hidden = !message;
    }

    showCsvError(message) {
      if (!this.csvErrorEl) return;
      this.csvErrorEl.textContent = message;
      this.csvErrorEl.hidden = !message;
    }

    handleTemplateDownload(event) {
      event.preventDefault();
      const blob = new Blob([CSV_HEADERS + '\n'], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Wildgrain Gift Recipient List.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    }

    async handleFileUpload(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;

      this.showCsvError('');
      try {
        const rows = await this.readSpreadsheetRows(file);
        const recipients = this.recipientsFromRows(rows);
        if (!recipients.length) {
          this.showCsvError(this.t('csvError'));
          return;
        }
        /* Incomplete rows are allowed through so they show up on the list
           with per-row errors. Pay Now still requires a complete list. */
        this.draftRecipients = recipients;
        const name = file.name.replace(/\.(csv|xlsx|xls)$/i, '') || this.draftListName;
        this.persistDraftList(name);
        this.closeListModal();
      } catch (err) {
        this.showCsvError(this.t('csvExcelError'));
      }
    }

    async readSpreadsheetRows(file) {
      if (isExcelFile(file)) {
        const XLSX = await loadSheetJs();
        const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) return [];
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
          defval: '',
          raw: false,
        });
        return rows.map(stringifySheetRow).filter((row) => row.some((cell) => cell !== ''));
      }
      return this.parseCsvRows(await file.text());
    }

    /* Minimal CSV parser (quoted fields supported). First row must be headers. */
    parseCsvRows(text) {
      const rows = [];
      let row = [];
      let field = '';
      let inQuotes = false;
      const src = String(text || '').replace(/^\uFEFF/, '');

      for (let i = 0; i < src.length; i++) {
        const ch = src[i];
        if (inQuotes) {
          if (ch === '"') {
            if (src[i + 1] === '"') {
              field += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else {
            field += ch;
          }
        } else if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          row.push(field);
          field = '';
        } else if (ch === '\n' || ch === '\r') {
          if (ch === '\r' && src[i + 1] === '\n') i++;
          row.push(field);
          field = '';
          if (row.some((cell) => cell.trim() !== '')) rows.push(row);
          row = [];
        } else {
          field += ch;
        }
      }
      row.push(field);
      if (row.some((cell) => cell.trim() !== '')) rows.push(row);
      return rows;
    }

    /* Common header spellings are mapped onto the recipient fields. */
    recipientsFromRows(rows) {
      if (!rows || rows.length < 2) return [];

      const aliases = {
        firstname: 'firstName',
        first: 'firstName',
        lastname: 'lastName',
        last: 'lastName',
        name: 'firstName',
        email: 'email',
        recipientemail: 'email',
        emailaddress: 'email',
        phone: 'phone',
        telephone: 'phone',
        mobile: 'phone',
        cellphone: 'phone',
        cellphonenumber: 'phone',
        recipientcellphone: 'phone',
        recipientcellphonenumber: 'phone',
        company: 'company',
        commercial: 'commercial',
        commercialaddress: 'commercial',
        address: 'address1',
        address1: 'address1',
        street: 'address1',
        streetaddress: 'address1',
        address2: 'address2',
        apt: 'address2',
        unit: 'address2',
        suite: 'address2',
        aptsuitesfloor: 'address2',
        city: 'city',
        state: 'state',
        province: 'state',
        zip: 'zip',
        zipcode: 'zip',
        postal: 'zip',
        postalcode: 'zip',
        postcode: 'zip',
        zippostal: 'zip',
        country: 'country',
        note: 'note',
        giftnote: 'note',
        giftmessage: 'note',
        message: 'note',
      };

      const headers = rows[0].map((header) => {
        const normalized = String(header || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        if (aliases[normalized]) return aliases[normalized];
        /* Long template header: "Gift Note (optional; maximum is 240 characters...)" */
        if (normalized.startsWith('giftnote')) return 'note';
        if (normalized.includes('cellphone') || normalized.includes('phonenumber')) return 'phone';
        if (normalized.includes('email')) return 'email';
        return null;
      });

      return rows
        .slice(1)
        .map((cells) => {
          const recipient = {};
          headers.forEach((key, index) => {
            if (!key || !cells[index] || !cells[index].trim()) return;
            const value = cells[index].trim();
            if (key === 'commercial') {
              recipient.commercial = /^(1|true|yes|y)$/i.test(value);
            } else if (key === 'note') {
              recipient.note = this.sanitizeRecipientNote(value);
            } else {
              recipient[key] = value;
            }
          });
          if (recipient.zip) recipient.zip = padUsZip(recipient.zip);
          if (!recipient.country) recipient.country = 'US';
          return recipient;
        })
        .filter((recipient) => Object.keys(recipient).some((key) => key !== 'country'))
        .slice(0, MAX_RECIPIENTS);
    }

    t(key) {
      const fallbacks = {
        remove: 'Remove',
        decrease: 'Decrease quantity',
        increase: 'Increase quantity',
        quantity: 'Quantity',
        edit: 'Edit',
        list: 'List',
        editList: 'Edit recipient list',
        createList: 'Create recipient list',
        enterRecipients: 'Enter recipients',
        addFirstRecipient: 'Add your first recipient',
        addRecipientTitle: 'Add a recipient',
        uploadRecipients: 'Upload your recipients',
        recipient: 'recipient',
        recipients: 'recipients',
        listEmpty: 'Add at least one recipient before saving.',
        csvError: "We couldn't find any recipients in that file. Check the column headers and try again.",
        csvExcelError: "We couldn't read that Excel file. Try again, or save it as CSV and upload that instead.",
        csvFixRetry: 'Fix these rows and upload the file again.',
        csvMoreErrors: 'And __COUNT__ more.',
        recipientRequired: 'First name, last name, address, city, state, and ZIP code are required.',
        recipientInvalidEmail: 'Enter a valid email, or leave it blank.',
        recipientMissing: '__WHO__ is missing __FIELDS__.',
        recipientInvalidEmailNamed: '__WHO__ has an invalid email.',
        recipientInvalidPhoneNamed: '__WHO__ has an invalid phone number.',
        recipientUnshippableStateNamed: '__WHO__ is in __STATE__. Unfortunately we do not ship there yet. Remove this recipient or use a different address.',
        recipientStateUnshippable: 'Unfortunately we do not ship to __STATE__ yet. Please use a different address.',
        giftNotePreview: 'On __WHO__’s card: “__NOTE__”',
        giftNotePreviewSample: 'a sample recipient',
        giftNotePreviewEmpty: 'nothing would print',
        giftNoteTokenNoRoom: 'No room for __TOKEN__ — the note would go over __MAX__ characters.',
        giftNoteUnknownToken: "__TOKEN__ isn't a field we can fill in. Use __ALLOWED__.",
        giftNoteUnknownTokenNamed: "__WHO__ has __TOKEN__ in their gift note, which isn't a field we can fill in. Use __ALLOWED__.",
        giftNoteEmpty: 'Write a gift note first.',
        giftNoteNoRecipients: 'Add a recipient before applying a gift note.',
        giftNoteAllHaveNotes: 'Every recipient already has a gift note.',
        giftNoteTooLongFor: 'This note is over __MAX__ characters. Each first and last name counts as 14 toward the limit.',
        giftNoteReplaceTitle: 'Replace __COUNT__ existing gift notes?',
        giftNoteReplaceTitleOne: 'Replace 1 existing gift note?',
        giftNoteReplaceApply: 'Replace __COUNT__ notes',
        giftNoteReplaceApplyOne: 'Replace 1 note',
        giftNoteReplaceMore: 'And __COUNT__ more.',
        giftNoteApplied: 'Applied to __COUNT__ recipients.',
        giftNoteAppliedOne: 'Applied to 1 recipient.',
        perRecipient: 'per recipient',
        removeRecipient: 'Remove recipient',
        subtotalRecipients: 'Subtotal (__COUNT__ recipients)',
        subtotalOneRecipient: 'Subtotal (1 recipient)',
        countryUs: 'United States of America',
        countryCa: 'Canada',
        buyerNameRequired: 'Enter your name to continue.',
        buyerEmailRequired: 'Enter a valid work email to continue.',
        buyerPhoneInvalid: 'Enter a valid 10-digit US phone, or leave it blank.',
        recipientsNeedFix: '__COUNT__ recipients need fixes before checkout — see highlighted rows above.',
        payNow: 'Pay Now',
        payLoading: 'Preparing checkout…',
        payError: 'Something went wrong preparing checkout. Please try again.',
        apiMissing: 'Checkout is not configured. Set the API base URL in the theme section settings.',
        logoUpload: 'Upload logo',
        logoReplace: 'Replace logo',
        logoUploading: 'Uploading…',
        logoError: "We couldn't upload that logo. Please try again.",
        logoInvalidType: "That file type isn't supported. Please choose a PNG, JPG, WEBP or SVG image.",
        logoTooLarge: 'That image is larger than 2MB. Please resize it and try again.',
        logoNetwork: "We couldn't reach the server. Check your internet connection and try again.",
        logoRateLimited: 'Too many uploads in a short time. Please wait a minute and try again.',
        logoUnexpected: 'The upload failed with an unexpected server response (status __STATUS__). Please try again shortly.',
        scheduleHoldHint: "We'll start shipping on this date. Boxes typically arrive 1–3 business days later. We can't promise a delivery day with frozen shipping.",
        scheduleWeekend: "That ship date is a weekend — we'll roll it to Monday.",
        schedulePast: 'Pick today or a future date.',
        schedulePickDate: 'Pick a date, or choose ship as soon as possible.',
      };
      const attr = 'text' + key.charAt(0).toUpperCase() + key.slice(1);
      return this.dataset[attr] || fallbacks[key];
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const format = this.dataset.moneyFormat || '${{amount}}';
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }
  }

  customElements.define('corporate-order-details', CorporateOrderDetails);
}
